import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

const LandfillMethane = ({ onClose, isModal = false }) => {
  const [, setIsVideoPlaying] = useState(false);
  const [displayedDescription, setDisplayedDescription] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  // Project data
  const projectData = {
    title: "LAKHODAIR VALORIZATION PROJECT",
    description:
      "The Project aims to capture and utilize landfill gas emissions for clean energy generation.",
    highlights: [
      "Will produce 35–50 GWh of renewable energy annually",
      "Will generate approximately USD 5.5 million per year in combined power sales and carbon revenues",
      "~190,000 tCO₂e/year emissions reduction",
      "USD 30–35 million total investment",
      "700–1,000 green jobs created",
      "Clean energy generation through gas emissions",
    ],
  };

  useEffect(() => {
    if (currentIndex < projectData.description.length) {
      const timer = setTimeout(() => {
        setDisplayedDescription(
          (prev) => prev + projectData.description[currentIndex]
        );
        setCurrentIndex((prev) => prev + 1);
      }, 30); // Adjust speed here (lower = faster)

      return () => clearTimeout(timer);
    }
  }, [currentIndex, projectData.description]);
  const handleBack = () => onClose?.();

  // Handle video end
  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        width: "100%",
        margin: "auto",
        background:
          "linear-gradient(135deg, #0d3b2a 0%, #1b5e20 50%, #2d8659 100%)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        padding: "20px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.4)",
      }}
    >
      {/* Back Button - Only show in modal mode */}
      {isModal && (
        <IconButton
          onClick={handleBack}
          sx={{
            position: "absolute",
            top: "10px",
            left: "10px",
            zIndex: 100,
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            border: "2px solid rgba(255, 255, 255, 0.3)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              transform: "scale(1.1)",
            },
          }}
        >
          <ArrowBack />
        </IconButton>
      )}

      {/* Main Content Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          flex: 1,
          gap: "10px",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            textAlign: "center",
            animation: "fadeInDown 1s ease-out",
            flexShrink: 0,
            width: "100%",
            "@keyframes fadeInDown": {
              "0%": { opacity: 0, transform: "translateY(-30px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontSize: "2.5rem",
              fontWeight: 800,
              textAlign: "center",
              textShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
              background: "linear-gradient(135deg, #ffffff 0%, #e0f7fa 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "slideInFromTop 1s ease-out",
              marginBottom: "10px",
              lineHeight: 1.2,
              "@keyframes slideInFromTop": {
                "0%": { opacity: 0, transform: "translateY(-30px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
              "@media (max-width: 768px)": {
                fontSize: "1.5rem",
              },
            }}
          >
            ⚡ {projectData.title} ⚡
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontSize: "1.3rem",
              textAlign: "center",
              margin: "0 auto",
              lineHeight: 1.3,
              minHeight: "1.5rem",
              fontFamily: "monospace",
            }}
          >
            {displayedDescription}
            {currentIndex < projectData.description.length && (
              <span
                style={{
                  animation: "blink 1s infinite",
                  marginLeft: "2px",
                }}
              >
                |
              </span>
            )}
            <style>
              {`
                                   @keyframes blink {
                                     0%, 50% { opacity: 1; }
                                     51%, 100% { opacity: 0; }
                                   }
                                 `}
            </style>
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
          }}
        >
          {projectData.highlights.map((highlight, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 8px",
                ml: "5px",
                background: "rgba(255,255,255,0.1)",
                borderRadius: "6px",
                border: "1px solid rgba(255,255,255,0.2)",
                width: "49%", // 👈 lets width fit the content
              }}
            >
              <Box
                sx={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #ffd700 0%, #ff6b00 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "2rem",
                }}
              >
                ✓
              </Box>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "1.3rem",
                  lineHeight: 1.1,
                  width: "100%",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {highlight}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            // minWidth: "280px",
            width: "800px",
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
            background: "#000",
          }}
        >
          <video
            controls={"true"}
            autoPlay
            // muted
            loop
            src="videos/wasteTovalue/p4.mp4"
            preload="metadata"
            onEnded={handleVideoEnd}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LandfillMethane;
